/* InstallPrompt — PWA installation banner with iOS-aware instructions.
 * Detects iOS Safari (no beforeinstallprompt) and shows "Add to Home Screen" guide.
 * On other browsers, uses beforeinstallprompt for native install banner. */
import React, { useState, useEffect } from 'react';
import { Share, PlusSquare, Download, X, Home, Bell, Sparkles } from 'lucide-react';

const DISMISS_KEY = 'fc_pwa_install_dismissed_v1';

function detectPlatform() {
  if (typeof window === 'undefined') return { ios: false, standalone: false, android: false };
  const ua = window.navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  return { ios: isIOS, android: isAndroid, standalone: isStandalone };
}

export default function InstallPrompt() {
  const [platform, setPlatform] = useState({ ios: false, standalone: false, android: false });
  const [installEvent, setInstallEvent] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const doInstall = async () => {
    if (installEvent) {
      installEvent.prompt();
      const result = await installEvent.userChoice;
      if (result.outcome === 'accepted') dismiss();
      setInstallEvent(null);
    } else if (platform.ios) {
      setShowIOSGuide(true);
    }
  };

  // Already installed — celebrate silently
  if (platform.standalone) return null;
  // User dismissed
  if (dismissed && !showIOSGuide) return null;

  const canInstall = !!installEvent || platform.ios;
  if (!canInstall) return null;

  return (
    <>
      {!showIOSGuide && (
        <div
          className="rounded-2xl p-4 border relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #FFE8D0 0%, #FFD5F0 100%)', borderColor: '#E5B588' }}
          data-testid="pwa-install-banner"
        >
          <button
            onClick={dismiss}
            className="absolute top-2 right-2 p-1 rounded-full bg-white/60 hover:bg-white text-amber-800"
            aria-label="Stäng"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-start gap-3">
            <div className="text-4xl leading-none flex-shrink-0">🐾</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-amber-950">
                Gör Farmor Corgi till en riktig app 💜
              </div>
              <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
                {platform.ios
                  ? 'På iPhone måste appen ligga på hemskärmen — då kan Farmor ringa dig även när Safari är stängd.'
                  : 'Installera Focus Critter så finns Farmor alltid nära — även när webbläsaren är stängd.'}
              </p>
              <button
                onClick={doInstall}
                data-testid="pwa-install-btn"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold shadow"
              >
                {platform.ios ? <Home className="w-3 h-3" /> : <Download className="w-3 h-3" />}
                {platform.ios ? 'Visa hur på iPhone' : 'Installera nu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showIOSGuide && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowIOSGuide(false)}
          data-testid="pwa-ios-guide"
        >
          <div
            className="rounded-3xl p-6 max-w-sm w-full shadow-2xl bg-gradient-to-br from-amber-50 to-rose-50 border border-amber-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIOSGuide(false)}
              className="absolute p-1.5 rounded-full bg-white/70 hover:bg-white text-slate-500"
              style={{ top: 12, right: 12 }}
              aria-label="Stäng"
              data-testid="pwa-ios-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-center mb-4">
              <div className="text-5xl">🐶</div>
              <h2 className="text-xl font-bold text-amber-950 mt-1" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
                Lägg till på hemskärmen
              </h2>
              <p className="text-xs text-amber-900/80 mt-1">
                På iPhone/iPad behövs 3 steg — det tar en halv minut 💜
              </p>
            </div>

            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">1</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                    Tryck på <Share className="w-4 h-4 text-sky-500" /> Dela-knappen
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">Den finns nere i mitten (Safari) eller uppe till höger.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">2</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                    Välj <PlusSquare className="w-4 h-4 text-slate-700" /> <em>"Lägg till på hemskärmen"</em>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">Scrolla ner om du inte ser det direkt.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">
                    Tryck <em>"Lägg till"</em>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">Öppna Farmor Corgi från hemskärmen — inte Safari.</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-emerald-900">
                    Aktivera notiser sedan
                  </div>
                  <div className="text-xs text-emerald-800 mt-0.5 flex items-start gap-1">
                    <Bell className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>I appen — under Farmor Corgi. Först då kan hon ringa dig 🐾</span>
                  </div>
                </div>
              </li>
            </ol>

            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => { dismiss(); setShowIOSGuide(false); }}
                data-testid="pwa-ios-got-it"
                className="text-xs px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              >
                Klart, jag fixar det 💜
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
